import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { switchMap, shareReplay, map } from 'rxjs/operators';
import { Observable, throwError, of } from 'rxjs';
import { Country } from '../models/country';
import { Timezone } from '../models/timezone';
import { PhoneCode } from '../models/phone';
import { Occupation } from '../models/occupation';
import { BusinessRevenue } from '../models/business-revenue';
import { SalaryRange } from '../models/salary-range';
import { TotalNetWorth } from '../models/total-net-worth';
import { UserLocale } from '../models/user-locale';
import { AuthService } from './auth.service';
import { ProfileService } from './profile.service';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {

  totalNetWorthsRequest: Observable<SalaryRange[]>;
  salaryRangesRequest: Observable<SalaryRange[]>;
  businessRevenuesRequest: Observable<BusinessRevenue[]>;
  occupationsRequest: Observable<Occupation[]>;
  phoneCodesRequest: Observable<PhoneCode[]>;
  timezonesRequest: Observable<Timezone[]>;
  countriesRequest: Observable<Country[]>;
  userLocaleRequest: Observable<UserLocale>;

  constructor(private api: ApiService, private auth: AuthService, private profileService: ProfileService) { }

  userLocale() {
    if (this.userLocaleRequest) {
      return this.userLocaleRequest;
    }

    this.userLocaleRequest = this.api.get(`{
      userLocale {   
        country {
            id,
            name,
            code,
            code3,
            gcc
        },
        currency {
            name,
            code
        }
      }   
    }`, null, false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['userLocale']);
      }),
      this.api.unifyErrors(this.userLocaleRequest),
      shareReplay()
    );

    return this.userLocaleRequest;
  }

  userCurrency(): Observable<string> {
    if (this.auth.getToken()) {
      return this.profileService.getPreferences().pipe(
        map(response => response.fiatCurrencyCode || 'AED')
      );
    } else {
      return this.userLocale().pipe(
        map(response => response && response.currency ? response.currency.code : 'AED')
      );
    }
  }

  countries(): Observable<Country[]> {
    if (this.countriesRequest) {
      return this.countriesRequest;
    }

    this.countriesRequest = this.api.get(`{
      countries{
        id
        name
        code
        code3
        gcc
      }
    }`, null, false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['countries']);
      }),
      this.api.unifyErrors(this.countriesRequest),
      shareReplay()
    );

    return this.countriesRequest;
  }

  getCountry(id: number|string): Observable<Country> {
    return this.countries().pipe(
      map<Country[], Country>(response => {
        if (!response) {
          return null;
        }

        return response.find(item => item.id === id);
      })
    );
  }

  timezones(): Observable<Timezone[]> {
    if (this.timezonesRequest) {
      return this.timezonesRequest;
    }

    this.timezonesRequest = this.api.get(`{
      timeZones{
        name
        utcOffsetSec
      }
    }`, null, false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['timeZones']);
      }),
      this.api.unifyErrors(this.salaryRangesRequest),
      shareReplay()
    );

    return this.timezonesRequest;
  }

  phoneCodes(): Observable<PhoneCode[]> {
    if (this.phoneCodesRequest) {
      return this.phoneCodesRequest;
    }

    this.phoneCodesRequest = this.api.get(`{
      phoneCodes{
        id
        code
        name
        country{
          id
          name
          code
          code3
          gcc
        }
        callSupported
      }
    }`, null, false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['phoneCodes']);
      }),
      this.api.unifyErrors(this.salaryRangesRequest),
      shareReplay()
    );

    return this.phoneCodesRequest;
  }

  occupations(): Observable<Occupation[]> {
    if (this.occupationsRequest) {
      return this.occupationsRequest;
    }

    this.occupationsRequest = this.api.get(`{
      occupations{
        id
        name
        isOccupationInfoNeeded
        isSelfEmploymentInfoNeeded
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['occupations']);
      }),
      this.api.unifyErrors(this.salaryRangesRequest),
      shareReplay()
    );

    return this.occupationsRequest;
  }

  businessRevenues(): Observable<BusinessRevenue[]> {
    if (this.businessRevenuesRequest) {
      return this.businessRevenuesRequest;
    }

    this.businessRevenuesRequest = this.api.get(`{
      t2BusinessRevenues{
        id
        text
        minValue{amount currency}
        maxValue{amount currency}
        period
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['t2BusinessRevenues']);
      }),
      this.api.unifyErrors(this.salaryRangesRequest),
      shareReplay()
    );

    return this.businessRevenuesRequest;
  }

  salaryRanges(): Observable<SalaryRange[]> {
    if (this.salaryRangesRequest) {
      return this.salaryRangesRequest;
    }

    this.salaryRangesRequest = this.api.get(`{
      t2SalaryRanges{
        id
        text
        minValue{amount currency}
        maxValue{amount currency}
        period
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['t2SalaryRanges']);
      }),
      this.api.unifyErrors(this.salaryRangesRequest),
      shareReplay()
    );

    return this.salaryRangesRequest;
  }

  totalNetWorths(): Observable<TotalNetWorth[]> {
    if (this.totalNetWorthsRequest) {
      return this.totalNetWorthsRequest;
    }

    this.totalNetWorthsRequest = this.api.get(`{
      t3TotalNetWorths{
        id
        text
        minValue{amount currency}
        maxValue{amount currency}
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['t3TotalNetWorths']);
      }),
      this.api.unifyErrors(this.totalNetWorthsRequest),
      shareReplay()
    );

    return this.totalNetWorthsRequest;
  }
}
